import React from 'react';
import Spinner from '@atlaskit/spinner';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';
import { EventDTO, IndicatorType, SloDTO } from '@atlassiansox/phobos-client-ts';

import { joinEventsToSlo, SLO, SloIncidents } from '@/ui';
import { useFetchErrorController } from '@/controllers';
import { flexCenter, flexStart, marginAuto, spaceGapCompact, spaceTopComfortable } from '@/styles';
import { apiFetch } from '@/services';
import { CapabilitySLOEvents, SloEventsFromEventSummary } from '@/api';
import { applySloDefaults } from '@/views/slo-view/utils';
import { fetchSlosSpec, querySpec } from '@/services/api-service/api-queries';
import { largeSlosExperience } from '@/common/ufo/experiences';
import { UFOExperienceContext, useRestartUFOComponentExperience } from '@/common/ufo';

import SLOCard from '../../slo-card/large';
import CreateCard from '../../slo-card/create-card';
import { skeletonSLO } from '../constants';
import * as sharedStyles from '../styles';
import GroupCard from '../../slo-card/group-card';
import { isIndicatorTypeMimir } from '../../slo-group/utils';

import * as styles from './styles';

function LoadingLargeSlos(emptySLO: SLO) {
  return (
    <>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <SLOCard isSkeleton {...emptySLO} />
    </>
  );
}

type LargeSlosProps = {
  id: string;
};

const LargeSlos: React.FC<LargeSlosProps> = ({ id }) => {
  const { onError } = useFetchErrorController();

  const { queryKey: eventsKey, path: eventsPath } = querySpec('capability.{id}.events.summary', {
    id,
  });
  const { isInitialLoading: isLoadingEvents, data: eventsData } = useQuery(
    eventsKey,
    () => apiFetch(eventsPath),
    { onError }
  );

  const { queryKey: fetchSlosKey, fetchSlos } = fetchSlosSpec(id);
  const {
    isInitialLoading: isLoadingSlos,
    hasNextPage,
    fetchNextPage,
    data,
    error,
  } = useInfiniteQuery(fetchSlosKey, fetchSlos, {
    onError,
    getNextPageParam: (lastPage, _) => (lastPage.last ? undefined : (lastPage?.number ?? 0) + 1),
  });

  const { queryKey: incidentsKey, path: incidentsPath } = querySpec('hot.search.{id}', { id });
  const { isInitialLoading: isLoadingIncidents, data: incidentsData } = useQuery(
    incidentsKey,
    () => apiFetch(incidentsPath),
    { onError }
  );
  const incidents: SloIncidents = incidentsData?.content || [];
  const capabilityEvents: CapabilitySLOEvents[] = isLoadingEvents ? [] : eventsData || [];

  const [sentryRef] = useInfiniteScroll({
    loading: isLoadingSlos,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
    rootMargin: '0px 0px 400px 0px',
  });

  // FIXME: Logic that exists in CapabilityView that handles the case that data is undefined
  //  i.e. the isError case isn't defined here to fail the experience?
  //  The implication being that if the API call fails here, we'll show the Flag but
  //  not actually count the experience as having failed (for old bm3 instrumentation)
  return (
    <div css={[sharedStyles.sharedContainerStyles, styles.largeSlosGridContainerStyles]}>
      {(isLoadingEvents || isLoadingIncidents) && (
        <div css={[spaceTopComfortable, flexCenter]}>
          <div css={[spaceGapCompact, flexStart, styles.largeSlosLoadingStyles]}>
            <Spinner size="small" />
            <span>Loading alert events & incidents data...</span>
          </div>
        </div>
      )}
      <div css={[styles.largeSlosGridStyles(data?.pages[0].totalElements || 1)]}>
        {!isLoadingSlos && (data?.pages.length ?? 0) >= 1 ? (
          <>
            {data!.pages[0].content.length > 0 ? (
              data!.pages.flatMap(page =>
                page.content.map((s: SloDTO) => {
                  const slo = joinEventsToSlo(
                    SloEventsFromEventSummary(
                      capabilityEvents.find(e => e.slo?.publicId === s.publicId)?.events ?? []
                    ),
                    incidents.filter(incident => incident.sloPublicIds?.includes(s.publicId)),
                    applySloDefaults(s)
                  );
                  const isGroup: boolean =
                    slo.indicator?.type === IndicatorType.Grouped ||
                    isIndicatorTypeMimir(slo.indicator);
                  const events: EventDTO[] =
                    capabilityEvents.find(e => e.slo?.publicId === s.publicId)?.events || [];
                  return (
                    <React.Fragment key={slo.publicId}>
                      {isGroup && <GroupCard key={slo.publicId} {...slo} isLarge />}
                      {!isGroup && <SLOCard key={slo.publicId} {...slo} events={events} />}
                    </React.Fragment>
                  );
                })
              )
            ) : (
              <CreateCard />
            )}
            {hasNextPage && <Spinner ref={sentryRef} css={marginAuto} size="large" />}
            <UFOExperienceSuccess experience={largeSlosExperience} />
          </>
        ) : (
          <LoadingLargeSlos {...skeletonSLO} />
        )}
      </div>
    </div>
  );
};

const LargeSlosInstrumented: typeof LargeSlos = props => {
  useRestartUFOComponentExperience(largeSlosExperience, []);
  return (
    <UFOExperienceContext.Provider value={largeSlosExperience}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <LargeSlos {...props} />
    </UFOExperienceContext.Provider>
  );
};

export default React.memo(LargeSlosInstrumented);
